import {
  PERMISSION_TYPES,
  PERMISSION_MATRIX,
} from '@/constants/permissionMatrix';
let cachedMappings = null;
const buildMappings = () => {
  const legacyToMatrix = {};
  const funcToLegacy = {};
  const moduleStructure = {};

  Object.entries(PERMISSION_MATRIX).forEach(([module, groups]) => {
    moduleStructure[module] = groups;

    groups.forEach((group) => {
      group.functions.forEach((func) => {
        if (func.legacyMappings) {
          funcToLegacy[func.id] = func.legacyMappings;

          Object.entries(func.legacyMappings).forEach(
            ([permType, legacyKey]) => {
              if (!legacyToMatrix[legacyKey]) {
                legacyToMatrix[legacyKey] = [];
              }
              legacyToMatrix[legacyKey].push({
                module,
                group: group.group,
                funcId: func.id,
                permType,
              });
            },
          );
        }
      });
    });
  });

  return { legacyToMatrix, funcToLegacy, moduleStructure };
};

const getMappings = () => {
  if (!cachedMappings) {
    cachedMappings = buildMappings();
  }
  return cachedMappings;
};

export const resetMappingCache = () => {
  cachedMappings = null;
};

export const createEmptyMatrix = () => {
  const matrix = {};
  const { moduleStructure } = getMappings();

  Object.entries(moduleStructure).forEach(([module, groups]) => {
    matrix[module] = groups.map((group) => ({
      ...group,
      functions: group.functions.map((func) => ({
        ...func,
        permissions: Object.values(PERMISSION_TYPES).reduce((acc, type) => {
          acc[type] = false;
          return acc;
        }, {}),
      })),
    }));
  });

  return matrix;
};

const cloneMatrix = (matrix) => JSON.parse(JSON.stringify(matrix));

export const convertLegacyToMatrix = (legacyPermissions = []) => {
  const matrix = createEmptyMatrix();
  const { legacyToMatrix } = getMappings();

  legacyPermissions.forEach((legacyKey) => {
    const mappings = legacyToMatrix[legacyKey];
    if (mappings) {
      mappings.forEach(({ module, group, funcId, permType }) => {
        const matrixGroup = matrix[module]?.find((g) => g.group === group);
        if (matrixGroup) {
          const matrixFunc = matrixGroup.functions.find((f) => f.id === funcId);
          if (matrixFunc?.permissions) {
            matrixFunc.permissions[permType] = true;
          }
        }
      });
    }
  });

  updateAllPermissionsInMatrix(matrix);

  return matrix;
};

export const convertMatrixToLegacy = (matrix = {}) => {
  const { funcToLegacy } = getMappings();
  const legacySet = new Set();

  Object.entries(matrix).forEach(([, groups]) => {
    if (!Array.isArray(groups)) return;

    groups.forEach((group) => {
      if (!Array.isArray(group.functions)) return;

      group.functions.forEach((func) => {
        const legacyMappings = funcToLegacy[func.id];
        if (!legacyMappings || !func.permissions) return;

        Object.entries(func.permissions).forEach(
          ([permType, hasPermission]) => {
            if (hasPermission && permType !== PERMISSION_TYPES.ALL) {
              const legacyKey = legacyMappings[permType];
              if (legacyKey) {
                legacySet.add(legacyKey);
              }
            }
          },
        );
      });
    });
  });

  return Array.from(legacySet);
};

// Chỉ tính ALL dựa trên các type thực sự được map trong legacyMappings,
// không tính các type unmapped (mặc định false) để tránh false-negative.
const updateAllPermission = (func) => {
  if (!func.permissions) return;
  const mappedTypes = func.legacyMappings
    ? Object.keys(func.legacyMappings)
    : [];
  func.permissions[PERMISSION_TYPES.ALL] =
    mappedTypes.length > 0 && mappedTypes.every((type) => func.permissions[type]);
};

const updateAllPermissionsInMatrix = (matrix) => {
  Object.values(matrix).forEach((groups) => {
    if (!Array.isArray(groups)) return;

    groups.forEach((group) => {
      group.functions.forEach((func) => {
        updateAllPermission(func);
      });
    });
  });
};

const findFunction = (matrix, module, functionId) => {
  if (!matrix[module] || !Array.isArray(matrix[module])) {
    return null;
  }

  for (const group of matrix[module]) {
    const func = group.functions.find((f) => f.id === functionId);
    if (func) return func;
  }

  return null;
};

export const updateMatrixPermission = (
  matrix,
  module,
  functionId,
  permissionType,
  value,
) => {
  const newMatrix = cloneMatrix(matrix);
  const func = findFunction(newMatrix, module, functionId);

  if (!func?.permissions) return newMatrix;

  func.permissions[permissionType] = value;

  if (permissionType === PERMISSION_TYPES.ALL) {
    // Chỉ toggle các type được map, không chạm unmapped types
    const mappedTypes = func.legacyMappings ? Object.keys(func.legacyMappings) : [];
    mappedTypes.forEach((type) => {
      func.permissions[type] = value;
    });
  } else {
    updateAllPermission(func);
  }

  return newMatrix;
};

export const updateFunctionPermissions = (
  matrix,
  module,
  functionId,
  permissions,
) => {
  const newMatrix = cloneMatrix(matrix);
  const func = findFunction(newMatrix, module, functionId);

  if (!func) return newMatrix;

  func.permissions = { ...permissions };
  updateAllPermission(func);

  return newMatrix;
};

export const updateModulePermissions = (
  matrix,
  module,
  permissionType,
  value,
) => {
  let newMatrix = cloneMatrix(matrix);

  if (!newMatrix[module] || !Array.isArray(newMatrix[module])) {
    return newMatrix;
  }

  newMatrix[module].forEach((group) => {
    group.functions.forEach((func) => {
      newMatrix = updateMatrixPermission(
        newMatrix,
        module,
        func.id,
        permissionType,
        value,
      );
    });
  });

  return newMatrix;
};

export const getMatrixPermission = (
  matrix,
  module,
  functionId,
  permissionType,
) => {
  const func = findFunction(matrix, module, functionId);
  return func?.permissions?.[permissionType] || false;
};

export const hasAnyPermissions = (matrix, module, functionId) => {
  const func = findFunction(matrix, module, functionId);

  if (!func?.permissions) return false;

  return Object.entries(func.permissions)
    .filter(([type]) => type !== PERMISSION_TYPES.ALL)
    .some(([, value]) => value);
};

export const hasModulePermissions = (matrix, module) => {
  if (!matrix[module] || !Array.isArray(matrix[module])) {
    return false;
  }

  return matrix[module].some((group) =>
    group.functions.some((func) => hasAnyPermissions(matrix, module, func.id)),
  );
};

export const validateMatrix = (matrix) => {
  const errors = [];
  const warnings = [];
  const { moduleStructure } = getMappings();

  Object.entries(moduleStructure).forEach(([module, groups]) => {
    if (!matrix[module]) {
      warnings.push(`Module "${module}" not found in matrix`);
      return;
    }

    if (!Array.isArray(matrix[module])) {
      errors.push(`Module "${module}" is not an array`);
      return;
    }

    groups.forEach((group) => {
      group.functions.forEach((func) => {
        const matrixFunc = findFunction(matrix, module, func.id);

        if (!matrixFunc) {
          warnings.push(
            `Function "${func.id}" not found in module "${module}"`,
          );
          return;
        }

        if (!matrixFunc.permissions) {
          errors.push(`Function "${func.id}" has no permissions object`);
          return;
        }

        // Validate permission types
        Object.values(PERMISSION_TYPES).forEach((type) => {
          if (typeof matrixFunc.permissions[type] !== 'boolean') {
            errors.push(
              `Invalid permission type "${type}" for function "${func.id}" (expected boolean, got ${typeof matrixFunc.permissions[type]})`,
            );
          }
        });
      });
    });
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};
